import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, FolderOpen } from 'lucide-react';
import { fetchApplicationKitSummary, fetchMyJobs, safeError } from '../data/candidateApi';

function kitCopy(summary) {
  const used = Number(summary?.successful_kit_count ?? summary?.successful_kits_used ?? summary?.used ?? 0);
  const allowance = Number(summary?.free_successful_kits ?? summary?.free_kit_allowance ?? 4) || 4;
  return `Free application kits: ${Math.min(used, allowance)} of ${allowance} used.`;
}

export default function DocumentsPage() {
  const [jobs, setJobs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const [jobPayload, kitPayload] = await Promise.all([fetchMyJobs(), fetchApplicationKitSummary()]);
        if (!alive) return;
        setJobs(jobPayload.items);
        setSummary(kitPayload);
      } catch (err) {
        if (alive) setError(safeError(err));
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, []);

  const documentJobs = useMemo(() => jobs.filter((job) => job.cv_ready || job.cover_letter_ready || job.cv_status === 'completed' || job.cover_letter_status === 'completed'), [jobs]);

  return (
    <main className="min-h-screen bg-[#f6efe3] px-5 py-10 text-[#22362f]" data-testid="documents-page">
      <div className="mx-auto max-w-6xl">
        <p className="text-xs uppercase tracking-[0.18em] text-[#7b694d]">Stage 3</p>
        <h1 className="mt-3 font-serif-display text-4xl font-medium sm:text-5xl" data-testid="documents-title">Documents</h1>
        <div className="jmp-panel mt-6 p-5" data-testid="kit-allowance-banner">
          <p className="text-sm font-medium">{kitCopy(summary)}</p>
          <p className="mt-1 text-sm text-[#5f6c64]">Application package includes exactly Tailored CV and Customizable Cover Letter.</p>
        </div>
        {loading && <div className="mt-6 border border-[#d8c8ab] bg-[#fff9ee] p-6" data-testid="documents-loading">Loading documents…</div>}
        {error && <div className="mt-6 border border-red-200 bg-red-50 p-6 text-sm text-red-700" data-testid="documents-error">{error}</div>}
        {!loading && !error && documentJobs.length === 0 && (
          <section className="jmp-panel mt-6 p-8" data-testid="documents-empty-state">
            <FolderOpen className="h-8 w-8 text-[#7b694d]" aria-hidden="true" />
            <h2 className="mt-4 font-serif-display text-3xl font-medium">No documents yet</h2>
            <p className="mt-3 text-sm text-[#5f6c64]">Prepare an application from My Jobs and the finished documents will appear here.</p>
          </section>
        )}
        <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid="documents-list">
          {documentJobs.map((job) => {
            const id = job.job_result_id || job.id;
            return (
              <article key={id} className="jmp-card-interactive p-5" data-testid={`document-job-${id}`}>
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-[#ECE7DC]"><FileText className="h-6 w-6 text-[#10231D]" aria-hidden="true" /></span>
                <h2 className="mt-4 font-serif-display text-2xl font-medium" data-testid={`document-job-title-${id}`}>{job.title || job.job_title || 'Untitled role'}</h2>
                <p className="mt-1 text-sm text-[#5f6c64]">{job.company || 'Company not listed'}</p>
                <ul className="mt-4 space-y-2 text-sm" data-testid={`document-artifact-list-${id}`}>
                  <li data-testid={`document-tailored-cv-${id}`}>Tailored CV: {job.cv_ready || job.cv_status === 'completed' ? 'Ready' : 'Not ready'}</li>
                  <li data-testid={`document-cover-letter-${id}`}>Customizable Cover Letter: {job.cover_letter_ready || job.cover_letter_status === 'completed' ? 'Ready' : 'Not ready'}</li>
                </ul>
                <Link to={`/application-package/${encodeURIComponent(id)}`} className="btn-secondary mt-5 inline-flex items-center px-4 text-sm" data-testid={`document-open-package-${id}`}>Open package</Link>
              </article>
            );
          })}
        </div>
      </div>
    </main>
  );
}